(() => {
  const SECTION_SELECTOR = '.minaco-product-reviews--editorial';
  const ALLOWED_SORTS = [
    { match: /most recent/i, label: 'Most Recent' },
    { match: /highest rating|high.*rating/i, label: 'Highest Rating' }
  ];
  const ANSWER_ORDER = new Map([
    ['height', 0],
    ['weight', 1],
    ['usual size', 2],
    ['size purchased', 3],
    ['cup size', 4]
  ]);
  const WRITE_REVIEW_TRIGGER_SELECTOR = '[data-testid="write-review-button"], .jdgm-write-rev-link, .jm-write-review-button';

  function getNativeReviewButtons(section) {
    const knownButtons = [...section.querySelectorAll(WRITE_REVIEW_TRIGGER_SELECTOR)]
      .map((element) => element.matches('button, a') ? element : element.closest('button, a') || element);
    const textMatches = [...section.querySelectorAll('button, a')]
      .filter((element) => /write a review/i.test(element.textContent.trim()));

    return [...new Set([...knownButtons, ...textMatches])]
      .filter((button) => !button.classList.contains('minaco-review-gate-button'));
  }

  function normalizedLabel(value) {
    return value.trim().toLowerCase().replace(/\s+/g, ' ').replace(/:$/, '');
  }

  function createProductFallback(section, review) {
    const nativeCard = review.querySelector('.jm-product-variant-card:not(.minaco-review-product-fallback)');
    const existingFallback = review.querySelector('.minaco-review-product-fallback');

    if (nativeCard) {
      existingFallback?.remove();
      return;
    }

    if (existingFallback || !section.dataset.productTitle) return;

    const stack = review.querySelector(':scope > .jm-stack');
    if (!stack) return;

    const card = document.createElement('div');
    card.className = 'jm-product-variant-card minaco-review-product-fallback';
    card.setAttribute('data-minaco-product-context', 'current-product');

    const cluster = document.createElement('div');
    cluster.className = 'jm-cluster';

    if (section.dataset.productImage) {
      const imageLink = document.createElement('a');
      imageLink.className = 'minaco-review-product-image-link';
      imageLink.href = section.dataset.productUrl || '#';
      imageLink.setAttribute('aria-label', `View ${section.dataset.productTitle}`);

      const image = document.createElement('img');
      image.className = 'jm-product-variant-card__thumbnail minaco-review-product-image';
      image.src = section.dataset.productImage;
      image.alt = section.dataset.productImageAlt || section.dataset.productTitle;
      image.width = 160;
      image.height = 200;
      image.loading = 'lazy';
      imageLink.append(image);
      cluster.append(imageLink);
    }

    const content = document.createElement('div');
    content.className = 'jm-product-variant-card__content';

    const link = document.createElement('a');
    link.className = 'jm-product-variant-card__product-link';
    link.href = section.dataset.productUrl || '#';
    link.textContent = section.dataset.productTitle;

    content.append(link);
    cluster.append(content);
    card.append(cluster);
    stack.prepend(card);
  }

  function normalizeProductCard(section, review) {
    const card = review.querySelector('.jm-product-variant-card');
    if (!card) return;

    const content = card.querySelector('.jm-product-variant-card__content');
    const productLink = card.querySelector('.jm-product-variant-card__product-link');
    if (!content || !productLink) return;

    productLink.setAttribute('title', productLink.textContent.trim());

    const thumbnail = card.querySelector('.jm-product-variant-card__thumbnail');
    if (thumbnail && !thumbnail.closest('a')) {
      const imageLink = document.createElement('a');
      imageLink.className = 'minaco-review-product-image-link';
      imageLink.href = productLink.href || section.dataset.productUrl || '#';
      imageLink.setAttribute('aria-label', `View ${productLink.textContent.trim()}`);
      thumbnail.parentNode.insertBefore(imageLink, thumbnail);
      imageLink.append(thumbnail);
    }

    if (card.classList.contains('minaco-review-product-fallback')) return;

    const directTexts = [...content.querySelectorAll(':scope > .jm-text')];
    const variant = content.querySelector('.jm-product-variant-card__variant-title')
      || directTexts.find((element) => !/^reviewing$/i.test(element.textContent.trim()));
    if (!variant) return;

    variant.classList.add('minaco-product-variant-value');
    if (!variant.dataset.minacoOriginalVariant) {
      const originalVariant = variant.textContent.trim();
      variant.dataset.minacoOriginalVariant = originalVariant;
      variant.setAttribute('title', originalVariant);
      const colorIndex = Number.parseInt(section.dataset.colorOptionIndex || '-1', 10);
      const optionValues = originalVariant.split(/\s*\/\s*/);
      if (colorIndex >= 0 && optionValues[colorIndex]) {
        variant.textContent = optionValues[colorIndex];
      }
    }

    if (!content.querySelector('.minaco-product-separator')) {
      const separator = document.createElement('span');
      separator.className = 'minaco-product-separator';
      separator.setAttribute('aria-hidden', 'true');
      separator.textContent = '—';
      content.insertBefore(separator, variant);
    }
  }

  function normalizeAnswerRows(review) {
    review.querySelectorAll('.jm-multi-choice-answer').forEach((row) => {
      const children = [...row.children];
      const label = children[0];
      const value = children.at(-1);
      if (!label || !value || label === value) return;

      label.classList.add('minaco-review-answer-label');
      value.classList.add('minaco-review-answer-value');
      value.classList.toggle('minaco-review-answer-value--numeric', /\d/.test(value.textContent));

      const order = ANSWER_ORDER.get(normalizedLabel(label.textContent));
      row.style.order = String(order ?? 99);
    });
  }

  function normalizeEmptyStates(section) {
    let isEmpty = false;

    section.querySelectorAll('.jm-text, p, [role="status"], [role="alert"]').forEach((node) => {
      if (node.closest('.minaco-custom-empty-review-state')) return;
      if (node.children.length > 0) return;
      const text = node.textContent.trim().replace(/\s+/g, ' ');
      if (/^be the first to write a review$/i.test(text) || /^no items found$/i.test(text)) {
        node.classList.add('minaco-review-empty-state');
        isEmpty = true;
      }
    });

    section.classList.toggle('is-empty', isEmpty);
    return isEmpty;
  }

  function normalizeReview(section, review) {
    createProductFallback(section, review);
    normalizeProductCard(section, review);
    normalizeAnswerRows(review);

    review.querySelectorAll('.jm-verified-buyer-badge').forEach((badge) => {
      const badgeText = badge.querySelector('.jm-verified-buyer-badge__text');
      if (!badgeText) return;

      if (badgeText.textContent.trim() !== 'VERIFIED BUYER') badgeText.textContent = 'VERIFIED BUYER';
      const identity = badge.parentElement;
      if (!identity) return;

      identity.classList.add('minaco-reviewer-identity--verified');
      if (!identity.querySelector(':scope > .minaco-verified-separator')) {
        const separator = document.createElement('span');
        separator.className = 'minaco-verified-separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = '—';
        identity.insertBefore(separator, badge);
      }
    });
  }

  function openAccessDialog(section, state) {
    const dialog = section.querySelector('[data-minaco-review-access-dialog]');
    if (!dialog) return;

    const title = dialog.querySelector('[data-minaco-review-access-title]');
    const message = dialog.querySelector('[data-minaco-review-access-message]');
    const login = dialog.querySelector('[data-minaco-review-login]');

    if (state === 'signed-out') {
      title.textContent = 'SIGN IN TO REVIEW';
      message.textContent = 'Sign in to your MINA&CO account so we can confirm that this product is in your order history.';
      login.hidden = false;
    } else if (state === 'not-purchased') {
      title.textContent = 'PURCHASE REQUIRED';
      message.textContent = 'We could not find this product in your MINA&CO order history. Reviews are reserved for customers who purchased this item.';
      login.hidden = true;
    } else {
      title.textContent = 'REVIEW FORM UNAVAILABLE';
      message.textContent = 'We confirmed your purchase, but the Judge.me review form is not available. Please refresh the page or contact MINA&CO.';
      login.hidden = true;
    }

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  function ensureReviewGate(section) {
    const nativeButtons = getNativeReviewButtons(section);
    const nativeButton = nativeButtons[0];
    const isEmpty = section.classList.contains('is-empty');

    nativeButtons.forEach((button) => {
      button.classList.add('minaco-native-review-button');
      button.setAttribute('aria-hidden', 'true');
      button.tabIndex = -1;
    });

    const gateButtons = [...section.querySelectorAll('.minaco-review-gate-button')];
    let gateButton = gateButtons.shift();
    gateButtons.forEach((button) => button.remove());
    if (!gateButton) {
      const header = section.querySelector('.jm-review-widget-minimal-header');
      if (!header && !isEmpty) return;

      gateButton = document.createElement('button');
      gateButton.type = 'button';
      gateButton.className = 'jm-button jm-button--primary minaco-review-gate-button';
      gateButton.textContent = 'WRITE A REVIEW';

      gateButton.addEventListener('click', () => {
        const currentNativeButton = getNativeReviewButtons(section)[0];
        const state = section.dataset.reviewGateState || 'signed-out';

        if (state === 'eligible' && currentNativeButton) {
          currentNativeButton.click();
          return;
        }

        openAccessDialog(section, state === 'eligible' ? 'unavailable' : state);
      });
    }

    let emptyState = section.querySelector('.minaco-custom-empty-review-state');
    if (isEmpty) {
      if (!emptyState) {
        const widget = section.querySelector('.minaco-product-reviews__widget');
        if (!widget) return;

        emptyState = document.createElement('div');
        emptyState.className = 'minaco-custom-empty-review-state';

        const message = document.createElement('p');
        message.className = 'minaco-custom-empty-review-state__message';
        message.textContent = 'Be the first to write a review';
        emptyState.append(message);
        widget.prepend(emptyState);
      }
      if (gateButton.parentElement !== emptyState) emptyState.append(gateButton);
    } else {
      emptyState?.remove();
      const header = section.querySelector('.jm-review-widget-minimal-header');
      if (header && !header.contains(gateButton)) {
        if (nativeButton) nativeButton.insertAdjacentElement('afterend', gateButton);
        else header.append(gateButton);
      }
    }

    const dialog = section.querySelector('[data-minaco-review-access-dialog]');
    if (dialog && dialog.dataset.minacoBound !== 'true') {
      dialog.dataset.minacoBound = 'true';
      dialog.querySelector('[data-minaco-review-access-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    }
  }

  function closeFilter(section) {
    if (!section) return;
    const filterButton = section.querySelector('.jm-custom-answer-filter');
    section.querySelectorAll('.jm-filter-panel').forEach((panel) => {
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
    });
    if (filterButton) {
      filterButton.setAttribute('aria-expanded', 'false');
    }
    section.classList.remove('is-filter-open');
  }

  function enhanceFilterSelect(select) {
    if (select.dataset.minacoFilterEnhanced === 'true') return;
    const panel = select.closest('.jm-filter-panel');
    if (!panel || select.options.length === 0) return;

    select.dataset.minacoFilterEnhanced = 'true';
    select.classList.add('minaco-review-filter__native');
    select.closest('label')?.classList.add('minaco-review-filter__control');

    const menu = document.createElement('div');
    menu.className = 'minaco-review-filter__menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', select.getAttribute('aria-label') || 'Filter reviews');

    [...select.options].forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'minaco-review-filter__option';
      item.setAttribute('role', 'option');
      item.dataset.value = option.value;
      item.textContent = option.textContent;
      item.setAttribute('aria-selected', String(option.selected));
      item.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        menu.querySelectorAll('[role="option"]').forEach((candidate) => {
          candidate.setAttribute('aria-selected', String(candidate === item));
        });
        closeFilter(select.closest(SECTION_SELECTOR));
      });
      menu.append(item);
    });

    select.insertAdjacentElement('afterend', menu);
  }

  function closeSort(sort) {
    const button = sort.querySelector('.minaco-review-sort__trigger');
    const menu = sort.querySelector('.minaco-review-sort__menu');
    if (!button || !menu) return;
    sort.classList.remove('is-open');
    button.setAttribute('aria-expanded', 'false');
    menu.hidden = true;
  }

  function enhanceSort(select) {
    if (select.dataset.minacoEnhanced === 'true') return;

    const kept = [];
    ALLOWED_SORTS.forEach((allowed) => {
      const option = [...select.options].find((item) => allowed.match.test(item.textContent.trim()));
      if (option && !kept.includes(option)) {
        option.textContent = allowed.label;
        kept.push(option);
      }
    });

    if (kept.length < 2) return;
    [...select.options].forEach((option) => {
      if (!kept.includes(option)) option.remove();
    });

    select.dataset.minacoEnhanced = 'true';
    select.classList.add('minaco-review-sort__native');

    const sort = select.closest('.jm-sort-filter');
    if (!sort || sort.querySelector('.minaco-review-sort')) return;

    const custom = document.createElement('div');
    custom.className = 'minaco-review-sort';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'minaco-review-sort__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    const triggerText = document.createElement('span');
    triggerText.textContent = select.selectedOptions[0]?.textContent || kept[0].textContent;
    trigger.append(triggerText);

    const menu = document.createElement('div');
    menu.className = 'minaco-review-sort__menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', select.getAttribute('aria-label') || 'Sort reviews');
    menu.hidden = true;

    kept.forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'minaco-review-sort__option';
      item.setAttribute('role', 'option');
      item.dataset.value = option.value;
      item.textContent = option.textContent;
      item.setAttribute('aria-selected', String(option.selected));
      item.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        triggerText.textContent = option.textContent;
        menu.querySelectorAll('[role="option"]').forEach((candidate) => {
          candidate.setAttribute('aria-selected', String(candidate === item));
        });
        closeSort(sort);
        trigger.focus();
      });
      menu.append(item);
    });

    trigger.addEventListener('click', () => {
      const opening = !sort.classList.contains('is-open');
      document.querySelectorAll('.jm-sort-filter.is-open').forEach(closeSort);
      if (opening) {
        const section = sort.closest(SECTION_SELECTOR);
        const filterButton = section?.querySelector('.jm-custom-answer-filter[aria-expanded="true"]');
        filterButton?.click();
        menu.hidden = false;
        requestAnimationFrame(() => sort.classList.add('is-open'));
        trigger.setAttribute('aria-expanded', 'true');
      }
    });

    custom.append(trigger, menu);
    sort.append(custom);
  }

  function enhanceSection(section) {
    if (!section.isConnected) return;

    getNativeReviewButtons(section).forEach((button) => {
      if (button.textContent.trim() !== 'WRITE A REVIEW') button.textContent = 'WRITE A REVIEW';
    });

    section.querySelectorAll('.jm-custom-answer-filter__text').forEach((label) => {
      if (label.textContent.trim() !== 'FILTERS') label.textContent = 'FILTERS';
    });

    section.querySelectorAll('.jm-sort-filter__select').forEach(enhanceSort);
    section.querySelectorAll('.jm-review-item').forEach((review) => normalizeReview(section, review));
    normalizeEmptyStates(section);
    ensureReviewGate(section);

    const filterButton = section.querySelector('.jm-custom-answer-filter');
    if (filterButton && filterButton.dataset.minacoBound !== 'true') {
      filterButton.dataset.minacoBound = 'true';
      filterButton.addEventListener('click', () => {
        section.querySelectorAll('.jm-sort-filter.is-open').forEach(closeSort);
      });
    }
    const widget = section.querySelector('.jm-review-widget');
    section.querySelectorAll('.jm-filter-panel').forEach((panel) => {
      panel.classList.add('minaco-filter-panel');
      panel.querySelectorAll('select').forEach(enhanceFilterSelect);
      if (panel.dataset.minacoFilterBound !== 'true') {
        panel.dataset.minacoFilterBound = 'true';
        panel.addEventListener('click', (event) => {
          const option = event.target.closest([
            '.minaco-review-filter__option',
            '.jm-tag',
            '.jm-rating-filter__tag',
            '[class*="rating-filter__tag"]',
            '[class*="clear-filter"]'
          ].join(','));
          if (option) {
            // Let Judge.me receive the click and update its result set first,
            // then close only the visual panel. Never click the toggle again:
            // that can reopen the panel during Judge.me's async rerender.
            setTimeout(() => closeFilter(section), 0);
            setTimeout(() => closeFilter(section), 180);
          }
        });
      }
      if (filterButton && widget) {
        const widgetRect = widget.getBoundingClientRect();
        const buttonRect = filterButton.getBoundingClientRect();
        panel.style.setProperty('--mprv-filter-left', `${Math.max(0, buttonRect.left - widgetRect.left)}px`);
        panel.style.setProperty('--mprv-filter-top', `${Math.max(0, buttonRect.bottom - widgetRect.top + 12)}px`);
      }
    });
    section.classList.toggle('is-filter-open', filterButton?.getAttribute('aria-expanded') === 'true');
  }

  function initializeSection(section) {
    if (section.dataset.minacoReviewsReady === 'true') {
      enhanceSection(section);
      return;
    }

    section.dataset.minacoReviewsReady = 'true';
    let scheduled = false;
    const scheduleEnhancement = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        enhanceSection(section);
      });
    };

    new MutationObserver(scheduleEnhancement).observe(section, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded']
    });

    enhanceSection(section);
  }

  function initializeAll(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) initializeSection(root);
    root.querySelectorAll?.(SECTION_SELECTOR).forEach(initializeSection);
  }

  document.addEventListener('click', (event) => {
    document.querySelectorAll('.jm-sort-filter.is-open').forEach((sort) => {
      if (!sort.contains(event.target)) closeSort(sort);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.jm-sort-filter.is-open').forEach((sort) => {
      closeSort(sort);
      sort.querySelector('.minaco-review-sort__trigger')?.focus();
    });
    document.querySelectorAll('[data-minaco-review-access-dialog][open]').forEach((dialog) => dialog.close());
  });

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  window.addEventListener('resize', () => initializeAll());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }
})();
